// A WEBSITE IN AN AI ANSWER — the preview package's views the assistant
// mounts in its thread and a Fly page places as sections (workspace
// functions/assistantAppViews.js, the `views[]` contract). Nika 2026-10-18:
// "test workspaces with more data, live websites, live previews … inspect
// modes" — "fly should cover all functionality that would be possible
// otherwise".
//
// previewAiViews() is the view PROVIDER. The components (SitePreviewView,
// ProjectPagesView, PreviewDesignSystemView) and these functions ride the
// preview EAGER slice, so a view mounts with no fold of the preview app; the
// one step that needs the app's render pipeline — INSPECT, the project in the
// sandboxed user-code frame with element picking — folds the preview context
// first (workspace ensureAppViewContext('preview')).
//
//   site-preview    one of the workspace's websites, live: `project` (an enum
//                   of the WEBSITES — never an installed module or a shared
//                   library — labelled by name), `path`, `env` (the project
//                   environment: its own published address, never another
//                   one silently), `device` (the preview's own device widths),
//                   `inspect`. In the card: env pills (the environments the
//                   project has on), a page switcher, inspect + pick, reload,
//                   open in the preview app, open the site in a new tab.
//   project-pages   a website's pages (and components) as screenshot tiles
//                   (functions/previewPagesAnswer.js); a tile swaps in the
//                   live page.
//   design-system   a website's tokens, type and icons through the design-
//                   system embed (functions/previewPagesAnswer.js).
//
// The projects are the canvas package's read (canvasAnswerRead — the home
// Projects widget's list) and its website rules (canvasAnswerSites,
// canvasAnswerNamed, canvasAnswerOnScreen). The addresses are the ones the
// preview's own "Open in new tab" builds: the project's mermaid address on
// THIS shell's channel (the host's channel, else the build's), per project
// environment. Nothing here writes project data.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function previewAiViews() {
  const el = this && typeof this.call === 'function' ? this : null
  const root = (el && el.state && el.state.root) || null
  // The site: one of the workspace's websites, an enum of their names once
  // the list is known (canvasAnswerProjectParam), a name-or-key before.
  let project = {
    type: 'string',
    describe: 'a website (project) name or key; the project on screen, else the newest website'
  }
  if (el && root) {
    try {
      const own = el.call('canvasAnswerProjectParam', root)
      if (own && typeof own === 'object') project = own
    } catch (_) {
      /* the string form stays */
    }
  }
  const env = {
    type: 'enum',
    values: ['production', 'staging', 'development'],
    labels: {
      production: 'preview.navbar.env.production',
      staging: 'preview.navbar.env.staging',
      development: 'preview.navbar.env.development'
    },
    default: 'production',
    describe: 'the project environment shown'
  }
  const open = { route: '/preview', call: 'previewAnswerLink' }
  return {
    app: {
      id: 'system/preview',
      navbar: [{ label: 'shell.app.studio', route: '/studio', icon: 'eye' }],
      apps: [{ icon: 'eye' }]
    },
    fold: null,
    views: [
      {
        id: 'site-preview',
        title: 'preview.answer.title',
        describe:
          "One of the workspace's websites, live: project, path = a page like /pricing, env, device; inspect = point at its elements.",
        component: 'SitePreviewView',
        icon: 'eye',
        hue: 'cerulean',
        // Half the width and tall (`l`, 6 × 6 of 12): a site at 16:10 reads
        // in it. `xl` (12 × 3) was a full-width strip of ~190 px on a Fly
        // page (QA on dev, 2026-10-18).
        size: 'l',
        params: {
          project,
          path: { type: 'string', describe: 'a page path, like / or /pricing' },
          env,
          device: {
            type: 'enum',
            values: ['desktop', 'tablet', 'mobile'],
            labels: {
              desktop: 'preview.answer.device.desktop',
              tablet: 'preview.answer.device.tablet',
              mobile: 'preview.answer.device.mobile'
            },
            default: 'desktop'
          },
          inspect: {
            type: 'boolean',
            default: false,
            describe: 'open with element inspect on (the person points at parts of the page)'
          }
        },
        link: open
      },
      {
        id: 'project-pages',
        title: 'preview.pagesView.title',
        describe:
          "A website's pages (or its components) as screenshot tiles; a tile opens that page live in the card.",
        component: 'ProjectPagesView',
        icon: 'pages',
        hue: 'cerulean',
        size: 'l',
        params: {
          project,
          env,
          show: {
            type: 'enum',
            values: ['pages', 'components'],
            labels: {
              pages: 'preview.sidebar.category.pages',
              components: 'preview.sidebar.category.components'
            },
            default: 'pages'
          },
          limit: { type: 'number', min: 1, max: 24, default: 12 }
        },
        link: open
      },
      {
        id: 'design-system',
        title: 'preview.dsView.title',
        describe:
          "A website's design system: its colors, typography, spacing, icons, transitions and layout tokens.",
        component: 'PreviewDesignSystemView',
        icon: 'color',
        hue: 'cerulean',
        size: 'l',
        params: {
          project,
          env,
          section: {
            type: 'enum',
            values: ['colors', 'typography', 'spacing', 'icons', 'transitions', 'layout'],
            labels: {
              colors: 'preview.sidebar.ds.colors',
              typography: 'preview.sidebar.ds.typography',
              spacing: 'preview.sidebar.ds.spacing',
              icons: 'preview.sidebar.ds.icons',
              transitions: 'preview.sidebar.ds.transitions',
              layout: 'preview.sidebar.ds.layout'
            },
            default: 'colors'
          }
        },
        link: open
      }
    ]
  }
}

// A project environment's name, as a bundle KEY (literal keys — never one
// built by concatenation, docs/I18N-CONVENTION.md §1.3).
export function previewEnvLabel(env) {
  const LABELS = {
    production: 'preview.navbar.env.production',
    staging: 'preview.navbar.env.staging',
    development: 'preview.navbar.env.development'
  }
  return LABELS[env] || LABELS.production
}

// The page path as the address takes it: one leading slash, no query, no
// fragment, no host — a path the model writes is a path, never a jump away.
export function previewAnswerPath(path) {
  const raw = String(path == null ? '' : path).trim()
  if (!raw || /^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//')) return '/'
  const clean = raw.split(/[?#]/)[0].replace(/\/{2,}/g, '/')
  if (!/^[\w\-./~%]*$/.test(clean)) return '/'
  return clean.startsWith('/') ? clean : '/' + clean
}

// The project's published address on THIS shell's channel for one project
// environment (`env`, production when left out), and the page on it; null
// when the owner or key cannot be resolved. Each environment has its own
// address — `<owner>--<key>--staging.<apex>` — never another one's (QA
// 2026-10-18 D3: a staging ask showed the production host).
export async function previewAnswerUrl(project, path, env) {
  const el = this
  const p = project && typeof project === 'object' ? project : null
  if (!p) return null
  const slugOf = (v) => {
    if (!v) return ''
    if (typeof v === 'string') return /^[a-f0-9]{24}$/i.test(v) ? '' : v
    return v.slug || v.key || ''
  }
  const owner = slugOf(p.owner) || slugOf(p.workspace && p.workspace.organization)
  const key = String(p.key || '').split('.')[0]
  if (!owner || !key) return null
  let channels = null
  try {
    channels = await import('@symbo.ls/channels')
  } catch (_) {
    channels = null
  }
  if (!channels || typeof channels.mermaidUrl !== 'function') return null
  let channel = null
  try {
    channel = el.call('currentChannelFromHost')
  } catch (_) {
    channel = null
  }
  const name =
    channel ||
    (typeof channels.currentChannel === 'function' ? channels.currentChannel() : undefined)
  const ENVS = ['production', 'staging', 'development']
  const base = channels.mermaidUrl(
    { owner, key, env: ENVS.includes(env) ? env : 'production' },
    name
  )
  if (!base) return null
  const page = el.call('previewAnswerPath', path)
  return page === '/' ? base + '/' : base + page
}

// THE SITE'S DATA — what a site view reads beside its frame, memoized per
// owner/key/environment on the ROOT element's scope for a minute (the
// userCodeFrame memo's own place), reads in flight shared: a Fly page with the
// site, its pages and its design system asks once.
//   default   the PUBLIC DATA PLANE: the anonymous public data route
//             (`sdk.getPublicProjectDataByKey`, gated on the PROJECT's
//             visibility). `{ published, visibility, data, error }`: data
//             when the project's data is public; when not, the project's
//             visibility. This is never what the site's address serves: the
//             render worker serves every project's site whatever its
//             visibility (previewSiteHosting) — a private project is a closed
//             data plane and editor, not a closed site.
//   auth      the editor's data for that environment (the preview's own read,
//             shared fetchProject getProjectData, raw — functions stay text
//             for the sandboxed frame). `{ data, error }`.
// A failed read answers `{ error }` and is not kept.
export async function previewSiteRead(req) {
  const host = this
  const r = req && typeof req === 'object' ? req : {}
  const owner = typeof r.owner === 'string' ? r.owner.trim() : ''
  const key = String(r.key || '').split('.')[0]
  const env = ['production', 'staging', 'development'].includes(r.env) ? r.env : 'production'
  const kind = r.auth ? 'auth' : 'public'
  if (!owner || !key) return { published: null, visibility: null, data: null, error: 'no_site' }
  let rootEl = null
  try {
    rootEl = typeof host.getRoot === 'function' ? host.getRoot() : null
  } catch (_) {
    rootEl = null
  }
  const memo = rootEl && rootEl.scope && typeof rootEl.scope === 'object' ? rootEl.scope : null
  if (memo && !(memo.previewSiteReads instanceof Map)) memo.previewSiteReads = new Map()
  const bag = memo ? memo.previewSiteReads : null
  const id = kind + ':' + owner + '/' + key + '/' + env
  const now = Date.now()
  const hit = bag && bag.get(id)
  if (hit && now - hit.at < 60000) return hit.promise
  const sdk = host.sdk || host.call('getSDK')
  const run = async () => {
    if (kind === 'auth') {
      const mod = await import('@symbo-ls/workspace-shared/functions/fetchProject.js')
      const data = await mod.getProjectData({ raw: true, owner, key, env })
      return { published: null, visibility: null, data: data || null, error: null }
    }
    if (!sdk || typeof sdk.getPublicProjectDataByKey !== 'function') {
      return { published: null, visibility: null, data: null, error: 'no_sdk' }
    }
    let data = null
    try {
      data = await sdk.getPublicProjectDataByKey({ owner, key }, { envKey: env })
    } catch (err) {
      if (err && err.code === 'password_required') {
        return { published: false, visibility: 'password-protected', data: null, error: null }
      }
      // The server's own answer rides the error's cause: an environment whose
      // mode is `published` on a project never published (`no_published_
      // version`, measured on dev 2026-10-19: system/bi-app production). The
      // public route fails closed while the site's address still serves the
      // latest saved version — `never`, not a failed read.
      const body = (err && err.cause && (err.cause.cause || err.cause)) || null
      if (body && typeof body === 'object' && body.error === 'no_published_version') {
        return { published: false, never: true, visibility: 'public', data: null, error: null }
      }
      throw err
    }
    if (data && typeof data === 'object') {
      return { published: true, visibility: 'public', data, error: null }
    }
    let visibility = null
    try {
      visibility =
        typeof sdk.getPublicProjectVisibility === 'function'
          ? await sdk.getPublicProjectVisibility({ owner, key })
          : null
    } catch (_) {
      visibility = null
    }
    return { published: false, visibility: visibility || null, data: null, error: null }
  }
  const promise = run().catch((err) => {
    if (bag && bag.get(id) && bag.get(id).promise === promise) bag.delete(id)
    return {
      published: null,
      visibility: null,
      data: null,
      error: String((err && err.message) || err || 'failed')
    }
  })
  if (bag) {
    bag.set(id, { at: now, promise })
    while (bag.size > 8) bag.delete(bag.keys().next().value)
  }
  return promise
}

// WHAT A SITE'S ADDRESSES SERVE — the platform's own answer, read as a member:
// the project record (`sdk.getProjectByKey` — `publishedVersion`, the release
// a `published`-mode environment serves) and its environments
// (`sdk.listEnvironments` — on or off, and the mode; the preview navbar's own
// rule). The render worker serves a site for EVERY project, whatever the
// project's visibility (server AuthorizationService._authGateway: the worker
// "serves every published site and custom domain"). Measured on dev
// 2026-10-19: system/invest, careers, community and content-and-marketing are
// private projects, and each production address answers 200 with the site to
// an anonymous visitor; a switched-off environment answers 404.
// `{ id, known, environments, on, published }`: `on` the environments
// switched on, in the publish order (all three when `known` is false — the
// environments could not be read); `published` true or false (the record
// names a published release or not), null when the record could not be read.
// Memoized beside the site reads.
export async function previewSiteHosting(req) {
  const host = this
  const r = req && typeof req === 'object' ? req : {}
  const ALL = ['production', 'staging', 'development']
  const owner = typeof r.owner === 'string' ? r.owner.trim() : ''
  const key = String(r.key || '').split('.')[0]
  const unknown = { id: r.id || null, known: false, environments: null, on: ALL, published: null }
  const data = (v) => (v && typeof v === 'object' && 'data' in v && v.success ? v.data : v)
  let rootEl = null
  try {
    rootEl = typeof host.getRoot === 'function' ? host.getRoot() : null
  } catch (_) {
    rootEl = null
  }
  const memo = rootEl && rootEl.scope && typeof rootEl.scope === 'object' ? rootEl.scope : null
  if (memo && !(memo.previewSiteReads instanceof Map)) memo.previewSiteReads = new Map()
  const bag = memo ? memo.previewSiteReads : null
  const slot = 'hosting:' + owner + '/' + key
  const hit = bag && bag.get(slot)
  if (hit && Date.now() - hit.at < 60000) return hit.promise
  const run = async () => {
    const sdk = host.sdk || host.call('getSDK')
    if (!sdk || !owner || !key) return unknown
    let id = r.id || null
    let published = null
    if (typeof sdk.getProjectByKey === 'function') {
      try {
        const p = data(await sdk.getProjectByKey({ owner, key }))
        if (p && typeof p === 'object' && (p._id || p.id)) {
          id = p._id || p.id
          const pv = p.publishedVersion
          published = !!(pv && (typeof pv === 'string' || pv.value || pv._id))
        }
      } catch (_) {
        published = null
      }
    }
    if (!id || typeof sdk.listEnvironments !== 'function') return { ...unknown, id, published }
    let map = null
    try {
      const res = data(await sdk.listEnvironments(id))
      map =
        res && res.environments && typeof res.environments === 'object' ? res.environments : null
    } catch (_) {
      map = null
    }
    if (!map) return { ...unknown, id, published }
    const on = ALL.filter((k) => map[k] && typeof map[k] === 'object' && map[k].enabled !== false)
    return { id, known: true, environments: map, on, published }
  }
  // An answer that knows nothing is not kept: the next read asks again.
  const promise = run()
    .catch(() => unknown)
    .then((out) => {
      if (!out.known && bag && bag.get(slot) && bag.get(slot).promise === promise) {
        bag.delete(slot)
      }
      return out
    })
  if (bag) {
    bag.set(slot, { at: Date.now(), promise })
    while (bag.size > 8) bag.delete(bag.keys().next().value)
  }
  return promise
}

// What ONE environment's address serves, from previewSiteHosting: 'off'
// (switched off, or not configured — the address answers 404), 'never' (mode
// `published` on a project never published — the address serves the latest
// saved version), 'published' (the published release), 'version' (a pinned
// version), 'branch' or 'latest' (the newest of a branch); null when the
// environments are not known. The canvas status card's environment rows read
// the same modes (canvas functions/canvasStatus.js canvasStatusEnvs).
export function previewEnvServes(hosting, env) {
  const h = hosting && typeof hosting === 'object' ? hosting : null
  if (!h || !h.known || !h.environments || typeof h.environments !== 'object') return null
  const cfg = h.environments[env]
  if (!cfg || typeof cfg !== 'object' || cfg.enabled === false) return 'off'
  if (cfg.mode === 'published') return h.published === false ? 'never' : 'published'
  if (cfg.mode === 'version' && typeof cfg.version === 'string' && cfg.version) return 'version'
  if (cfg.mode === 'branch') return 'branch'
  return 'latest'
}

// The pages a site's data declares, as addresses: real routes only (no
// pattern like `/blog/:id`), the home first, then in order, at most 60.
export function previewAnswerPages(data) {
  const pages = data && data.pages && typeof data.pages === 'object' ? data.pages : null
  if (!pages) return []
  const shell = (v) =>
    v != null && typeof v === 'object' && Object.keys(v).some((k) => k !== 'metadata')
  return Object.keys(pages)
    .filter((k) => k.startsWith('/') && !/[:*]/.test(k) && shell(pages[k]))
    .sort((a, b) => (a === '/' ? -1 : b === '/' ? 1 : a.localeCompare(b)))
    .slice(0, 60)
}

// Does the site's data render anything at all (an app shell or one page)?
export function previewAnswerHasContent(data) {
  if (!data || typeof data !== 'object') return false
  const shell = (v) =>
    v != null && typeof v === 'object' && Object.keys(v).some((k) => k !== 'metadata')
  if (shell(data.app)) return true
  const pages = data.pages && typeof data.pages === 'object' ? data.pages : {}
  return Object.keys(pages).some((k) => shell(pages[k]))
}

// THE SITE A VIEW SHOWS — the params (the model's), the choices made in the
// card (its own state: `env`, `page` — they win), the workspace's websites
// and the project on screen: `{ site, sites, read, notWebsite, storeApp }`;
// `site` (the canvas site row + `env`, `path`) null when no website matches,
// or none exists. What the asked name means is the canvas package's ONE rule
// (canvasAnswerNamed — the status card asks it too), through its load step
// canvasAnswerNamedRead (a name nothing matches reads the App Store list once
// when the shell has not read it yet): `notWebsite` names a project of this
// workspace that is not a website (an app, a module, a library — item 6f),
// `storeApp` an App Store app that is no project here (item 3); each its
// name, else its key — never an id.
export async function previewAnswerResolve(el) {
  const host = el || this
  const s = (host && host.state) || {}
  const params = (host && host.scope && host.scope.params) || {}
  const root = s.root || {}
  const ENVS = ['production', 'staging', 'development']
  const env = ENVS.includes(s.env) ? s.env : ENVS.includes(params.env) ? params.env : 'production'
  const path = host.call(
    'previewAnswerPath',
    typeof s.page === 'string' && s.page ? s.page : params.path
  )
  const installed = host.call('canvasAnswerInstalled', root)
  const read = await host.call('canvasAnswerRead', host)
  const sites = host.call('canvasAnswerSites', read && read.projects, installed)
  const meant =
    (await host.call(
      'canvasAnswerNamedRead',
      sites,
      read && !read.failed ? read.projects : null,
      params.project,
      host.call('canvasAnswerOnScreen', root),
      root
    )) || {}
  const site = meant.kind === 'website' ? meant.site : null
  return {
    site: site ? { ...site, env, path } : null,
    sites,
    read,
    notWebsite: meant.kind === 'project' ? meant.name : null,
    storeApp: meant.kind === 'storeApp' ? meant.name : null
  }
}

// THE ONE SENTENCE of a site view with no site to show (site-preview,
// project-pages, design-system; `s` the view's state, from
// previewAnswerResolve): a project of this workspace that is not a website
// says so with its name (item 6f), an App Store app that is no project here
// says that (item 3), a workspace with no website at all says so; else no
// website matches. Item 6f (2026-10-31): "No website in this workspace
// matches." read as "it does not exist" for a project the workspace has.
export function previewAnswerEmptyText(s) {
  const st = s && typeof s === 'object' ? s : {}
  const say = (key, name) => String(this.call('polyglot', key)).replace('{name}', () => name)
  if (st.notWebsite) return say('preview.answer.notWebsite', st.notWebsite)
  if (st.storeApp) return say('preview.answer.storeApp', st.storeApp)
  return String(this.call('polyglot', st.none ? 'preview.answer.none' : 'preview.answer.missing'))
}

// The inputs a site view's address follows, as one string: the params, the
// card's own choices, the installed apps (a module is not a website — they
// can land after the view: a thread restored at boot picked the console
// module of an app list still empty), the project on screen, and the card's
// counter of moved answers (canvasAnswerNamedMoved — a list that landed after
// the card answered changed what the name means; each load asks it first).
export function previewAnswerInputs(el) {
  const host = el || this
  const s = (host && host.state) || {}
  const params = (host && host.scope && host.scope.params) || {}
  const root = s.root || {}
  const on = host.call('canvasAnswerOnScreen', root)
  return JSON.stringify([
    params.project || '',
    params.path || '',
    params.env || '',
    s.env || '',
    s.page || '',
    host.call('canvasAnswerInstalled', root),
    on ? on.owner + '/' + on.key : '',
    Number(host && host.__canvasNamedEpoch) || 0
  ])
}

// The site-preview view's read, once per inputs (its OWN state). The frame
// mounts as soon as the address is known; the project's environments
// (previewSiteHosting) then say what that address serves — a switched-off
// environment is said in place of the frame (never another environment
// silently, QA 2026-10-18 D3), a never-published one under it — and fill the
// env pills; the site's data (public, else the editor's) lists the pages. The
// project's visibility never covers the frame: a private project's site
// serves every visitor (dev 2026-10-19, system/invest).
export async function previewAnswerLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const params = (host.scope && host.scope.params) || {}
  host.call('canvasAnswerNamedMoved', host, params.project)
  const sig = host.call('previewAnswerInputs', host)
  if (host.__previewAnswerSig === sig) return null
  host.__previewAnswerSig = sig
  const res = await host.call('previewAnswerResolve', host)
  if (host.__previewAnswerSig !== sig) return null
  const site = res.site
  const url = site ? await host.call('previewAnswerUrl', site, site.path, site.env) : null
  if (host.__previewAnswerSig !== sig) return null
  if (!site || !url) {
    // A name that is a project of this workspace but not a website, or an App
    // Store app that is no project here: say so, with its name
    // (previewAnswerResolve), never "no match".
    host.state.update({
      site: null,
      failed: true,
      // No name asked and no website at all: say so, never "no match".
      none: !params.project && !!res.read && !res.read.failed && !res.sites.length,
      notWebsite: res.notWebsite || null,
      storeApp: res.storeApp || null
    })
    return null
  }
  const before = host.state.site
  const same = !!before && before.url === url
  host.state.update({
    site: {
      key: site.key,
      name: site.name,
      owner: site.owner,
      id: site.id || null,
      env: site.env,
      path: site.path,
      url,
      serves: same ? before.serves : null,
      never: same ? before.never : false,
      pages: same ? before.pages : null,
      envs: same ? before.envs : null,
      content: same ? before.content : true
    },
    failed: false,
    none: false,
    notWebsite: null,
    storeApp: null,
    ...(same ? {} : { frameReady: null, frameSlow: null })
  })
  // An inspect the model asked for (`inspect: true`) starts once, when the
  // site is known.
  if (params.inspect === true && !host.__previewAnswerInspected) {
    host.__previewAnswerInspected = true
    host.call('previewAnswerInspect', host, true)
  }
  const [pub, hosting] = await Promise.all([
    host.call('previewSiteRead', { owner: site.owner, key: site.key, env: site.env }),
    host.call('previewSiteHosting', { owner: site.owner, key: site.key, id: site.id })
  ])
  if (host.__previewAnswerSig !== sig) return null
  const serves = host.call('previewEnvServes', hosting, site.env)
  // The page switcher: the public data, else (a private project, read as a
  // member) the editor's — the preview's own member read (previewSiteRead
  // `auth`); none for an environment that serves nothing.
  const view =
    pub && pub.data
      ? pub
      : serves === 'off'
        ? null
        : await host.call('previewSiteRead', {
            owner: site.owner,
            key: site.key,
            env: site.env,
            auth: true
          })
  if (host.__previewAnswerSig !== sig) return null
  const now = host.state.site
  if (!now || now.url !== url) return null
  host.state.update({
    site: {
      ...now,
      // 'off': the address serves nothing — said in place of the frame.
      serves,
      // Never published: the address still serves the latest saved version,
      // so the frame stays and one line says so (previewAnswerNote). The
      // record says it for a member; the public route for a public project.
      never: serves === 'never' || !!(pub && pub.never),
      pages: view && view.data ? host.call('previewAnswerPages', view.data) : null,
      // "No pages" only from the data the address serves — the public data.
      content: pub && pub.data ? host.call('previewAnswerHasContent', pub.data) : true,
      envs: hosting && Array.isArray(hosting.on) ? hosting.on : null
    }
  })
  return url
}

// What a site view's frame cannot say itself, as `{ text, cover }` (`text`
// '' when there is nothing to say): `cover` — a sentence IN PLACE of the
// frame (loading, the environment is switched off, the site has no pages, the
// inspect steps; QA 2026-10-18 D3 / D7), else one quiet line over its foot (a
// never-published environment, a slow site, the picker's hint). Never the
// project's visibility: a private or password-protected PROJECT still serves
// its site to every visitor (previewSiteHosting). `s` is the view's state;
// `this` an element of the card.
export function previewAnswerNote(s) {
  const el = this
  const st = s && typeof s === 'object' ? s : {}
  const site = st.site
  if (!site) return { text: '', cover: false }
  const say = (key) =>
    String(el.call('polyglot', key))
      .replace('{name}', () => site.name || site.key || '')
      .replace('{env}', () => String(el.call('polyglot', el.call('previewEnvLabel', site.env))))
  if (st.inspect) {
    if (st.inspectError) return { text: say('preview.answer.inspectFailed'), cover: true }
    if (!st.inspectReady) return { text: say('preview.answer.inspectLoading'), cover: true }
    const root = st.root || {}
    const node = el.call('previewAnswerInspectNode', el)
    if (root.picking && el.call('previewPickArmedOn', node)) {
      return { text: say('preview.answer.picking'), cover: false }
    }
    return { text: say('preview.answer.inspectReady'), cover: false }
  }
  if (site.serves === 'off') return { text: say('preview.answer.envOff'), cover: true }
  if (site.content === false) return { text: say('preview.answer.noContent'), cover: true }
  const frameKey = site.url + '#' + (Number(st.reload) || 0)
  if (site.never && st.frameReady === frameKey) {
    return { text: say('preview.answer.neverPublished'), cover: false }
  }
  if (st.frameReady === frameKey) return { text: '', cover: false }
  if (st.frameSlow === frameKey) return { text: say('preview.answer.slow'), cover: false }
  return { text: say('preview.answer.loading'), cover: true }
}

// The device a site view shows: the card's own choice, else the model's,
// else desktop.
export function previewAnswerDevice(s) {
  const st = s && typeof s === 'object' ? s : {}
  const params = (this && this.scope && this.scope.params) || {}
  const DEVICES = ['desktop', 'tablet', 'mobile']
  return DEVICES.includes(st.device)
    ? st.device
    : DEVICES.includes(params.device)
      ? params.device
      : 'desktop'
}

// A published frame came in (its `load`): the cover steps away.
export function previewAnswerFrameLoaded(el) {
  const frame = el || this
  const s = frame && frame.state
  if (!s || typeof s.update !== 'function' || !frame.frameKey) return false
  if (s.frameReady !== frame.frameKey) s.update({ frameReady: frame.frameKey })
  return true
}

// A published frame that has not come in after a while says so — the cover
// gives way to the frame (a slow site paints late: QA 2026-10-18 measured one
// at 15–20 s) with one quiet line.
export function previewAnswerFrameTimer(el) {
  const frame = el || this
  if (!frame || !frame.frameKey) return false
  clearTimeout(frame.__previewSlowTimer)
  frame.__previewSlowTimer = setTimeout(() => {
    const s = frame.state
    if (!s || typeof s.update !== 'function' || s.frameReady === frame.frameKey) return
    if (frame.node && frame.node.isConnected === false) return
    s.update({ frameSlow: frame.frameKey })
  }, 12000)
  return true
}

// The device's viewport — the preview's own device widths (the navbar's
// ResponsiveSwitching presets; components/Scene.js reads the same table).
// `desktop` / `tablet` / `mobile` name three of them. `[width, height]`, or
// null for an unknown device.
export function previewDeviceSize(device) {
  const DEVICES = {
    screenL: [1920, 1024],
    screenM: [1680, 1024],
    tabletL: [1366, 926],
    tabletS: [1024, 768],
    mobileXS: [375, 768]
  }
  const ALIASES = { desktop: 'screenM', tablet: 'tabletS', mobile: 'mobileXS' }
  const size = DEVICES[ALIASES[device] || device]
  return size ? size.slice() : null
}

// The frame box's size, read from the box itself (its own rect) into the
// view's state when it changed — on mount, on a window resize and after the
// layout settles (the grid sizes its cells after mount).
export function previewAnswerMeasure(el) {
  const box = el || this
  const node = box && box.node
  const s = box && box.state
  if (!node || !s || typeof s.update !== 'function') return false
  const read = () => {
    if (node.isConnected === false || typeof node.getBoundingClientRect !== 'function') return
    const r = node.getBoundingClientRect()
    const w = Math.round(r.width)
    const h = Math.round(r.height)
    if (!w || !h || (s.boxW === w && s.boxH === h)) return
    s.update({ boxW: w, boxH: h })
  }
  read()
  clearTimeout(box.__previewMeasureTimer)
  box.__previewMeasureTimer = setTimeout(read, 60)
  return true
}

// A frame's inline geometry: the device's own viewport, scaled down to the
// box (never up) — the site's media queries see the device width while the
// card shows all of it. A device narrower than the box sits centred.
export function previewAnswerFrameStyle(s) {
  const st = s && typeof s === 'object' ? s : {}
  const size = this.call('previewDeviceSize', this.call('previewAnswerDevice', st)) || [1680, 1024]
  const bw = Number(st.boxW) || 0
  const bh = Number(st.boxH) || 0
  if (!bw || !bh) return { width: '100%', height: '100%', left: '0', transform: 'none' }
  const scale = Math.min(1, bw / size[0])
  return {
    width: size[0] + 'px',
    height: Math.max(1, Math.round(bh / scale)) + 'px',
    left: scale < 1 ? '0' : Math.round((bw - size[0]) / 2) + 'px',
    transform: scale < 1 ? 'scale(' + scale.toFixed(4) + ')' : 'none'
  }
}

// THE CARD'S ↗ — the site in the preview app (`/preview?owner&key&env`, the
// sheet; ⌘ / Ctrl: a split), resolved from the params and the root's project
// list alone (a link is built before any view reads). null when no site can
// be named yet. A name that is an App Store app (no project here): its App
// Store page.
export function previewAnswerLink(params, root) {
  const r = root && typeof root === 'object' ? root : {}
  const p = params && typeof params === 'object' ? params : {}
  const list = this.call('canvasAnswerProjects', r)
  if (!Array.isArray(list)) return null
  const sites = this.call('canvasAnswerSites', list, this.call('canvasAnswerInstalled', r))
  // The card's own rule (canvasAnswerNamed): a project that is not a website
  // links nowhere, as its card shows no site; an App Store app that is no
  // project here links to its App Store page (canvasAnswerStoreLink).
  const meant =
    this.call(
      'canvasAnswerNamed',
      sites,
      list,
      p.project,
      this.call('canvasAnswerOnScreen', r),
      r
    ) || {}
  if (meant.kind === 'storeApp') return this.call('canvasAnswerStoreLink', meant.app)
  const site = meant.kind === 'website' ? meant.site : null
  if (!site || !site.owner || !site.key) return null
  const env = ['production', 'staging', 'development'].includes(p.env) ? p.env : 'production'
  return (
    '/preview?owner=' +
    encodeURIComponent(site.owner) +
    '&key=' +
    encodeURIComponent(site.key.split('.')[0]) +
    '&env=' +
    encodeURIComponent(env)
  )
}

// WHICH PROJECT the /preview page shows (pages/main.js, standalone and in the
// shell): its OWN address first — in the shell a sheet's route
// (`/preview?owner&key&env`, a Fly card's Open; the shell's readPageParam
// reads the pane's route, then the address bar), standalone the address bar
// — so a stale carrier never wins over it; else the Studio sidebar's carrier
// (`root._previewParams`). `{ owner, key, env, version }`, or null.
export function previewPageTarget(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const clean = (v) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  let q = null
  try {
    q = new URLSearchParams(
      typeof window !== 'undefined' && window.location ? window.location.search : ''
    )
  } catch (_) {
    q = null
  }
  const shellRead =
    !!el &&
    !!el.context &&
    !!el.context.functions &&
    typeof el.context.functions.readPageParam === 'function'
  const param = (name) => {
    let v = null
    try {
      v = shellRead ? el.call('readPageParam', name) : null
    } catch (_) {
      v = null
    }
    return clean(v) || clean(q ? q.get(name) : null)
  }
  const owner = param('owner')
  const key = param('key')
  if (owner && key) {
    // Version-pinned preview (canvas Version History → "Preview this
    // version") rides the same address.
    return { owner, key, env: param('env'), version: param('version') }
  }
  const p = r._previewParams
  if (p && clean(p.owner) && clean(p.key)) {
    return {
      owner: clean(p.owner),
      key: clean(p.key),
      env: clean(p.env),
      version: clean(p.version)
    }
  }
  return null
}

// The /preview page's state once its data landed: the data, and the
// environment the page names — the resolved one (a preview host's public
// read), else the one asked for, else none (the navbar's own default) — never
// a previous visit's (dev 2026-10-19: a Fly card opened production and the
// env switch read "Development", left from a development open before it).
export function previewPagePatch(data, target) {
  const d = data && typeof data === 'object' ? data : null
  const t = target && typeof target === 'object' ? target : {}
  const ENVS = ['production', 'staging', 'development']
  const asked = ENVS.includes(t.env) ? t.env : null
  return {
    data: d,
    env: (d && d._resolvedEnv) || asked,
    ...(d && d._resolvedEnv
      ? {
          resolvedEnv: d._resolvedEnv,
          requestedEnv: d._requestedEnv || d._resolvedEnv,
          usedFallback: !!d._usedFallback
        }
      : {})
  }
}

// Open a site in the preview app — the sheet (⌘ / Ctrl: a split): the target
// rides `root._previewParams` (the Studio sidebar's carrier, which the
// preview page reads first) and the pane's own route, so a reload restores it
// and a different site is a different pane route (the page mounts anew).
export function previewAnswerOpenSite(target, event) {
  const el = this
  const t = target && typeof target === 'object' ? target : {}
  const owner = typeof t.owner === 'string' ? t.owner.trim() : ''
  const key = String(t.key || '').split('.')[0]
  const root = el && el.state && el.state.root
  if (!owner || !key || !root || typeof root.update !== 'function') return false
  const env = ['production', 'staging', 'development'].includes(t.env) ? t.env : null
  root.update({ _previewParams: { owner, key, ...(env ? { env } : {}) } })
  const route =
    '/preview?owner=' +
    encodeURIComponent(owner) +
    '&key=' +
    encodeURIComponent(key) +
    (env ? '&env=' + encodeURIComponent(env) : '')
  const split = !!(event && (event.metaKey || event.ctrlKey))
  el.call('openAppPage', route, split ? { split: true } : undefined)
  return true
}

// The site-preview card's own controls. `el` is any element of the card: the
// view is found up the tree (its `dataSitePreview` mark).
export function previewAnswerView(el) {
  const from = el || this
  if (!from) return null
  if (from.dataSitePreview === true) return from
  return typeof from.lookup === 'function' ? from.lookup((e) => e.dataSitePreview === true) : null
}

// The card's inspect frame node (the iframe the user-code document mounts
// in), or null.
export function previewAnswerInspectNode(el) {
  const view = this.call('previewAnswerView', el)
  const frameEl =
    view && typeof view.lookdown === 'function' ? view.lookdown('SitePreviewInspectFrame') : null
  return (frameEl && frameEl.node) || null
}

// INSPECT — the site in the preview's sandboxed user-code frame (the editor's
// data for that environment through renderProject, the /preview pipeline)
// with element picking on: a pick lands in the composer (`root.
// pickedElements`, the chips) as on /preview. `on` false puts the published
// frame back and stops a picker armed on this card's frame.
export async function previewAnswerInspect(el, on) {
  const view = this.call('previewAnswerView', el)
  const s = view && view.state
  if (!view || !s || typeof s.update !== 'function') return false
  const node = view.call('previewAnswerInspectNode', view)
  if (!on) {
    if (node && s.root && s.root.picking && view.call('previewPickArmedOn', node)) {
      view.call('togglePick', node)
    }
    s.update({ inspect: false })
    return true
  }
  const site = s.site
  if (!site || !node) return false
  s.update({ inspect: true, inspectReady: false, inspectError: null })
  const token = (view.__previewInspectToken = (view.__previewInspectToken || 0) + 1)
  const fail = (code) => {
    if (view.__previewInspectToken !== token) return false
    s.update({ inspectError: code || 'failed' })
    return false
  }
  // The render pipeline is the preview app's: fold its context once.
  const fns = view.context && view.context.functions
  if (!fns || typeof fns.renderProject !== 'function') {
    const ok = await view.call('ensureAppViewContext', 'preview')
    if (!ok) return fail('fold')
  }
  const read = await view.call('previewSiteRead', {
    owner: site.owner,
    key: site.key,
    env: site.env,
    auth: true
  })
  if (view.__previewInspectToken !== token) return false
  if (!read || read.error || !read.data) return fail('data')
  let out = null
  try {
    out = await view.call('renderProject', node, read.data, { path: site.path })
  } catch (_) {
    out = null
  }
  if (view.__previewInspectToken !== token) return false
  if (!out || out.bootError) return fail('boot')
  s.update({ inspectReady: true })
  view.call('previewAnswerPickToggle', view, true)
  return true
}

// Arm (or stop) the element picker on this card's inspect frame — the eager
// togglePick with the frame named, so several frames can stand on one page.
// `want` true arms it (a no-op when it already is), false stops it, undefined
// toggles.
export function previewAnswerPickToggle(el, want) {
  const view = this.call('previewAnswerView', el)
  const s = view && view.state
  const node = view ? view.call('previewAnswerInspectNode', view) : null
  if (!s || !node || !s.inspectReady) return false
  const armed = !!(s.root && s.root.picking) && !!view.call('previewPickArmedOn', node)
  if (want === true && armed) return true
  if (want === false && !armed) return false
  return !!view.call('togglePick', node)
}

// RELOAD — the site again: this site's reads are dropped (the next read asks
// the server, not the minute-long memo), the frame is made anew, and an
// inspect frame renders the fresh data.
export function previewAnswerReload(el) {
  const view = this.call('previewAnswerView', el)
  const s = view && view.state
  if (!s || typeof s.update !== 'function' || !s.site) return false
  view.call('previewSiteForget', { owner: s.site.owner, key: s.site.key })
  view.__previewAnswerSig = null
  s.update({ reload: (Number(s.reload) || 0) + 1, frameReady: null, frameSlow: null })
  if (s.inspect) view.call('previewAnswerInspect', view, true)
  return true
}

// Drop a site's memoized reads (previewSiteRead / previewSiteHosting).
export function previewSiteForget(req) {
  const r = req && typeof req === 'object' ? req : {}
  const id = String(r.owner || '') + '/' + String(r.key || '').split('.')[0]
  let rootEl = null
  try {
    rootEl = typeof this.getRoot === 'function' ? this.getRoot() : null
  } catch (_) {
    rootEl = null
  }
  const bag = rootEl && rootEl.scope ? rootEl.scope.previewSiteReads : null
  if (!(bag instanceof Map)) return 0
  let n = 0
  for (const k of [...bag.keys()]) {
    const rest = k.slice(k.indexOf(':') + 1)
    if (rest === id || rest.startsWith(id + '/')) {
      bag.delete(k)
      n++
    }
  }
  return n
}

// The card leaves the page: a picker armed on its frame stops with it.
export function previewAnswerRemoved(el) {
  const view = el || this
  const node =
    view && typeof view.call === 'function' ? view.call('previewAnswerInspectNode', view) : null
  const root = view && view.state && view.state.root
  if (node && root && root.picking && view.call('previewPickArmedOn', node)) {
    view.call('togglePick', node)
    return true
  }
  return false
}
